import { useRef, useState, useEffect } from 'react';

const CURSIVE_FONTS = [
  { label: 'Signature 1', font: 'Dancing Script, cursive' },
  { label: 'Signature 2', font: 'Great Vibes, cursive' },
  { label: 'Signature 3', font: 'Pacifico, cursive' },
];

export default function SignaturePad({ label, value, onChange }) {
  const canvasRef  = useRef(null);
  const [mode, setMode]     = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');
  const [fontIdx, setFontIdx]     = useState(0);
  const [isEmpty, setIsEmpty]     = useState(true);
  const drawing = useRef(false);
  const lastPos = useRef({ x:0, y:0 });

  useEffect(() => {
    // Load Google Fonts for typed signatures
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Pacifico&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    drawing.current = true;
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
    setIsEmpty(false);
  };

  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    onChange(canvas.toDataURL('image/png'));
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    onChange('');
  };

  const applyTyped = () => {
    if (!typedName.trim()) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `52px ${CURSIVE_FONTS[fontIdx].font}`;
    ctx.fillStyle = '#1a1a2e';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName, canvas.width/2, canvas.height/2);
    setIsEmpty(false);
    onChange(canvas.toDataURL('image/png'));
  };

  useEffect(() => {
    if (mode === 'type' && typedName) applyTyped();
  }, [typedName, fontIdx]);

  // A signature saved earlier (e.g. the worker's, when a manager opens it to countersign) is drawn back in
  useEffect(() => {
    if (!value || !isEmpty) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      setIsEmpty(false);
    };
    img.src = value;
  }, [value]);

  return (
    <div>
      <div style={{ fontSize:'13px', fontWeight:'500', color:'var(--text)', marginBottom:'8px' }}>{label}</div>

      {/* Mode toggle */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'10px' }}>
        {['draw','type'].map(m=>(
          <button key={m} type="button" onClick={()=>{ setMode(m); clearCanvas(); setTypedName(''); }}
            style={{ flex:1, padding:'7px', borderRadius:'var(--radius)', border:`${mode===m?'1.5px solid var(--blue)':'1px solid var(--border-md)'}`, background:mode===m?'var(--blue-light)':'var(--surface)', color:mode===m?'var(--blue)':'var(--muted)', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' }}>
            <i className={`ti ${m==='draw'?'ti-pencil':'ti-keyboard'}`} style={{ marginRight:'4px' }} aria-hidden="true"/>
            {m==='draw'?'Draw signature':'Type name'}
          </button>
        ))}
      </div>

      {mode==='type' && (
        <div style={{ marginBottom:'10px' }}>
          <input value={typedName} onChange={e=>setTypedName(e.target.value)}
            placeholder="Type your full name..."
            style={{ width:'100%', padding:'8px 12px', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', fontSize:'14px', fontFamily:'inherit', boxSizing:'border-box', marginBottom:'8px' }}/>
          <div style={{ display:'flex', gap:'6px' }}>
            {CURSIVE_FONTS.map((f,i)=>(
              <button key={i} type="button" onClick={()=>setFontIdx(i)}
                style={{ flex:1, padding:'6px', border:`${fontIdx===i?'1.5px solid var(--blue)':'1px solid var(--border-md)'}`, borderRadius:'var(--radius)', background:fontIdx===i?'var(--blue-light)':'var(--surface)', cursor:'pointer', fontFamily:f.font, fontSize:'15px', color:fontIdx===i?'var(--blue)':'var(--muted)' }}>
                {typedName||'Sign'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Canvas */}
      <div style={{ position:'relative', border:`1.5px solid ${value?'var(--green)':'var(--border-md)'}`, borderRadius:'var(--radius)', background:'#fafafa', overflow:'hidden' }}>
        <canvas ref={canvasRef} width={600} height={160}
          style={{ width:'100%', height:'120px', display:'block', touchAction:'none', cursor:mode==='draw'?'crosshair':'default' }}
          onMouseDown={mode==='draw'?startDraw:undefined}
          onMouseMove={mode==='draw'?draw:undefined}
          onMouseUp={mode==='draw'?endDraw:undefined}
          onMouseLeave={mode==='draw'?endDraw:undefined}
          onTouchStart={mode==='draw'?startDraw:undefined}
          onTouchMove={mode==='draw'?draw:undefined}
          onTouchEnd={mode==='draw'?endDraw:undefined}
        />
        {isEmpty && (
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
            <span style={{ fontSize:'13px', color:'var(--hint)' }}>{mode==='draw'?'Sign here using finger or mouse':'Preview will appear here'}</span>
          </div>
        )}
        {!isEmpty && (
          <button type="button" onClick={clearCanvas}
            style={{ position:'absolute', top:'6px', right:'6px', background:'rgba(255,255,255,0.9)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', padding:'3px 8px', fontSize:'11px', color:'var(--muted)', cursor:'pointer' }}>
            Clear
          </button>
        )}
      </div>
      {value && <div style={{ fontSize:'11px', color:'var(--green)', marginTop:'4px' }}>✓ Signature captured</div>}
    </div>
  );
}
